import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Users, Mail, MessageSquare, Gift, UserPlus, BarChart3, RefreshCw,
  FileText, TrendingUp, Target, Phone, Settings, Globe, Megaphone,
  PenTool, Link2, Search, Shield, Save, Plus, Trash2, ExternalLink,
  Zap, Eye, ChevronRight, Circle, Percent
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIPELINE_STAGES = [
  { id: 'new', label: 'New', labelAr: 'جديد', color: 'bg-blue-500' },
  { id: 'contacted', label: 'Contacted', labelAr: 'تم التواصل', color: 'bg-indigo-500' },
  { id: 'qualified', label: 'Qualified', labelAr: 'مؤهل', color: 'bg-purple-500' },
  { id: 'proposal', label: 'Proposal', labelAr: 'عرض', color: 'bg-amber-500' },
  { id: 'approved', label: 'Approved', labelAr: 'معتمد', color: 'bg-emerald-500' },
  { id: 'funded', label: 'Funded', labelAr: 'تم التمويل', color: 'bg-green-600' },
];

import CampaignManager from '@/components/admin/CampaignManager';
import BlogEditor from '@/components/admin/BlogEditor';

const PLATFORM_CONFIG = {
  google: { label: 'Google Ads', color: 'bg-blue-500', icon: 'G' },
  meta: { label: 'Meta / Instagram', color: 'bg-pink-500', icon: 'M' },
  linkedin: { label: 'LinkedIn', color: 'bg-sky-600', icon: 'in' },
  tiktok: { label: 'TikTok', color: 'bg-gray-800', icon: 'TT' },
  youtube: { label: 'YouTube', color: 'bg-red-600', icon: 'YT' },
  twitter: { label: 'X / Twitter', color: 'bg-slate-900', icon: 'X' },
  snapchat: { label: 'Snapchat', color: 'bg-yellow-400', icon: 'SC' },
  whatsapp: { label: 'WhatsApp', color: 'bg-emerald-500', icon: 'WA' },
  email: { label: 'Email', color: 'bg-amber-500', icon: 'EM' },
  sms: { label: 'SMS', color: 'bg-indigo-500', icon: 'SM' },
  referral: { label: 'Referral', color: 'bg-purple-600', icon: 'R' },
  seo: { label: 'SEO / Organic', color: 'bg-teal-600', icon: 'SE' },
  influencer: { label: 'Influencer', color: 'bg-rose-500', icon: 'IN' },
  other: { label: 'Other', color: 'bg-gray-500', icon: '·' },
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-700' },
  scheduled: { label: 'Scheduled', cls: 'bg-sky-50 text-sky-700' },
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700' },
  paused: { label: 'Paused', cls: 'bg-amber-50 text-amber-700' },
  completed: { label: 'Completed', cls: 'bg-indigo-50 text-indigo-700' },
  archived: { label: 'Archived', cls: 'bg-slate-100 text-slate-500' },
};

const CAMPAIGN_TYPES = ['lead_gen', 'awareness', 'conversion', 'retargeting', 'email_nurture', 'brand', 'traffic', 'engagement'];
const GEO_OPTIONS = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'RAK', 'Fujairah', 'UAQ', 'GCC', 'International', 'UK', 'EU', 'India', 'Pakistan', 'KSA'];

export default function AdminPage() {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const df = isAr ? 'var(--font-ar-display)' : 'var(--font-en-display)';
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('overview');
  const [leads, setLeads] = useState([]);
  const [subscribers, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [chatbotLeads, setChatbot] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [settings, setSettings] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [articles, setArticles] = useState([]);
  const [rates, setRates] = useState({ resident: [], non_resident: [] });
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [statsR, settingsR, campaignsR, articlesR, ratesR] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/settings`, { withCredentials: true }),
        axios.get(`${API}/admin/campaigns`, { withCredentials: true }),
        axios.get(`${API}/blog/articles`),
        axios.get(`${API}/rates`),
      ]);
      setStats(statsR.data);
      setSettings(settingsR.data);
      setCampaigns(campaignsR.data);
      setArticles(articlesR.data);
      setRates({ resident: ratesR.data.resident || [], non_resident: ratesR.data.non_resident || [] });
    } catch {}
  }, []);

  const saveRates = async () => {
    setSaving(true);
    try {
      const clean = {
        resident: (rates.resident || []).filter(r => r.product || r.best_rate || r.rate_type),
        non_resident: (rates.non_resident || []).filter(r => r.product || r.best_rate || r.rate_type),
      };
      await axios.put(`${API}/admin/rates`, clean, { withCredentials: true });
      await fetchAll();
    } catch {}
    finally { setSaving(false); }
  };

  const resetRates = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/rates/reset`, {}, { withCredentials: true });
      await fetchAll();
    } catch {}
    finally { setSaving(false); }
  };

  const fetchTab = useCallback(async (t) => {
    try {
      if (t === 'leads' || t === 'pipeline') { const { data } = await axios.get(`${API}/admin/leads`, { withCredentials: true }); setLeads(data); }
      if (t === 'subscribers') { const { data } = await axios.get(`${API}/admin/subscribers`, { withCredentials: true }); setSubs(data); }
      if (t === 'contacts') { const { data } = await axios.get(`${API}/admin/contacts`, { withCredentials: true }); setContacts(data); }
      if (t === 'chatbot') { const { data } = await axios.get(`${API}/admin/chatbot-leads`, { withCredentials: true }); setChatbot(data); }
      if (t === 'referrals') { const { data } = await axios.get(`${API}/admin/referrals`, { withCredentials: true }); setReferrals(data); }
    } catch {}
  }, []);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) navigate('/login');
    else if (user?.role === 'admin') fetchAll();
  }, [user, authLoading, navigate, fetchAll]);

  useEffect(() => { if (!['overview', 'settings', 'campaigns', 'content', 'blog', 'social', 'seo', 'rates'].includes(tab)) fetchTab(tab); }, [tab, fetchTab]);

  const updateLeadStatus = async (phone, newStatus) => {
    try { await axios.put(`${API}/admin/leads/status`, { phone, status: newStatus }, { withCredentials: true }); fetchTab('leads'); fetchAll(); } catch {}
  };

  const saveSettings = async (updates) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, updates, { withCredentials: true });
      await fetchAll();
      // Notify public SiteSettingsProvider to refresh
      window.dispatchEvent(new Event('gmre:settings-updated'));
    } catch {}
    finally { setSaving(false); }
  };

  if (authLoading || !stats) return <div className="min-h-screen bg-[#F8FAFB] flex items-center justify-center pt-20"><div className="animate-spin w-8 h-8 border-4 border-[#A68966] border-t-transparent rounded-full" /></div>;

  const tabs = [
    { id: 'overview', label: isAr ? 'نظرة عامة' : 'Overview', icon: BarChart3 },
    { id: 'pipeline', label: isAr ? 'خط الأنابيب' : 'Pipeline', icon: TrendingUp },
    { id: 'leads', label: isAr ? 'العملاء' : 'Leads', icon: Users },
    { id: 'subscribers', label: isAr ? 'المشتركين' : 'Subscribers', icon: Mail },
    { id: 'contacts', label: isAr ? 'الرسائل' : 'Messages', icon: FileText },
    { id: 'chatbot', label: isAr ? 'البوت' : 'Chatbot', icon: MessageSquare },
    { id: 'referrals', label: isAr ? 'الإحالات' : 'Referrals', icon: Gift },
    { id: 'divider1' },
    { id: 'content', label: isAr ? 'إدارة المحتوى' : 'Content', icon: PenTool },
    { id: 'blog', label: isAr ? 'المدونة' : 'Blog', icon: FileText },
    { id: 'rates', label: isAr ? 'الأسعار' : 'Rates', icon: Percent },
    { id: 'campaigns', label: isAr ? 'الحملات' : 'Campaigns', icon: Megaphone },
    { id: 'social', label: isAr ? 'التواصل الاجتماعي' : 'Social Media', icon: Globe },
    { id: 'seo', label: 'SEO', icon: Search },
    { id: 'settings', label: isAr ? 'الإعدادات' : 'Settings', icon: Settings },
  ];

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F8FAFB] pt-20">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-[#0B2F44]" style={{ fontFamily: df }}>{isAr ? 'مركز عمليات جُليد' : 'Guleed Operations Center'}</h1>
            <p className="text-[#4A6572] text-xs mt-0.5">{isAr ? 'إدارة شاملة للمنصة الرقمية' : 'Full digital platform management'}</p>
          </div>
          <Button onClick={() => { fetchAll(); fetchTab(tab); }} variant="outline" size="sm" className="border-[#D0D8DC]"><RefreshCw className="w-3.5 h-3.5 me-1.5" />{isAr ? 'تحديث' : 'Refresh'}</Button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1.5 mb-6 pb-3 border-b border-[#D0D8DC]/30">
          {tabs.map(t => t.id.startsWith('divider') ? <div key={t.id} className="w-px h-8 bg-[#D0D8DC]/30 mx-1 self-center" /> : (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${tab === t.id ? 'bg-[#0B2F44] text-white shadow-sm' : 'text-[#4A6572] hover:bg-white hover:shadow-sm'}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* ═══ OVERVIEW ═══ */}
        {tab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KPI icon={Target} label={isAr ? 'إجمالي التفاعلات' : 'Total Interactions'} value={stats.total_lead_touches} dark />
              <KPI icon={Zap} label={isAr ? 'عملاء جدد' : 'New Leads'} value={stats.leads_new} color="blue" />
              <KPI icon={TrendingUp} label={isAr ? 'معدل التحويل' : 'Conversion'} value={`${stats.conversion_rate}%`} color="emerald" />
              <KPI icon={UserPlus} label={isAr ? 'مسجلين' : 'Users'} value={stats.registered_users} color="purple" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card title={isAr ? 'مصادر العملاء' : 'Lead Sources'} icon={BarChart3}>
                {Object.entries(stats.lead_sources).map(([k, v]) => <SourceBar key={k} label={k.replace(/_/g, ' ')} value={v} max={Math.max(...Object.values(stats.lead_sources), 1)} />)}
              </Card>
              <Card title={isAr ? 'خط الأنابيب' : 'Pipeline'} icon={TrendingUp}>
                {PIPELINE_STAGES.map(s => (
                  <div key={s.id} className="flex items-center gap-3 py-1.5">
                    <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                    <span className="text-xs text-[#4A6572] flex-1">{isAr ? s.labelAr : s.label}</span>
                    <span className="text-xs font-bold text-[#0B2F44]">{stats.pipeline[s.id] || 0}</span>
                  </div>
                ))}
              </Card>
            </div>
            <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                { l: isAr ? 'نماذج' : 'Forms', v: stats.leads },
                { l: isAr ? 'مشتركين' : 'Subs', v: stats.subscribers },
                { l: isAr ? 'رسائل' : 'Msgs', v: stats.contacts },
                { l: isAr ? 'بوت' : 'Bot', v: stats.chatbot_leads },
                { l: isAr ? 'إحالات' : 'Refs', v: stats.referrals },
                { l: isAr ? 'مسجلين' : 'Users', v: stats.registered_users },
              ].map((s, i) => (
                <div key={i} data-testid={`admin-stat-${i}`} className="bg-white rounded-lg p-3 border border-[#D0D8DC]/50 text-center">
                  <p className="text-lg font-bold text-[#0B2F44]">{s.v}</p>
                  <p className="text-[10px] text-[#4A6572]">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ PIPELINE ═══ */}
        {tab === 'pipeline' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {PIPELINE_STAGES.map(stage => {
              const sl = leads.filter(l => l.status === stage.id);
              return (
                <div key={stage.id} className="bg-white rounded-xl border border-[#D0D8DC]/50 overflow-hidden">
                  <div className={`${stage.color} px-3 py-2 flex items-center justify-between`}>
                    <span className="text-white text-[10px] font-bold">{isAr ? stage.labelAr : stage.label}</span>
                    <span className="text-white/80 text-[10px]">{sl.length}</span>
                  </div>
                  <div className="p-1.5 space-y-1.5 max-h-[350px] overflow-y-auto">
                    {sl.length === 0 ? <p className="text-[10px] text-[#4A6572] text-center py-4">—</p> : sl.map((l, i) => (
                      <div key={i} className="bg-[#F8FAFB] rounded-lg p-2 text-[10px] border border-[#D0D8DC]/30">
                        <p className="font-semibold text-[#0B2F44] truncate">{l.full_name}</p>
                        <p className="text-[#4A6572]">{l.phone}</p>
                        <Select value={l.status} onValueChange={v => updateLeadStatus(l.phone, v)}>
                          <SelectTrigger className="mt-1 h-6 text-[10px] border-[#D0D8DC]"><SelectValue /></SelectTrigger>
                          <SelectContent>{PIPELINE_STAGES.map(s => <SelectItem key={s.id} value={s.id} className="text-[10px]">{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ CONTENT MANAGER ═══ */}
        {tab === 'content' && settings && (
          <div className="space-y-4">
            <Card title={isAr ? 'محتوى الصفحة الرئيسية' : 'Homepage Content'} icon={PenTool} action={<Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">{isAr ? 'مباشر على الموقع' : 'Live on site'}</Badge>}>
              <p className="text-[11px] text-[#4A6572] mt-1 mb-3">{isAr ? 'اترك الحقل فارغاً لاستخدام النص الافتراضي.' : 'Leave a field empty to use the default site copy.'}</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'عنوان البطل (EN)' : 'Hero Title (EN)'}</label>
                  <Textarea data-testid="admin-hero-title-en" value={settings.content?.hero_title_en || ''} onChange={e => setSettings({...settings, content: {...settings.content, hero_title_en: e.target.value}})} rows={2} className="border-[#D0D8DC] text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'عنوان البطل (AR)' : 'Hero Title (AR)'}</label>
                  <Textarea data-testid="admin-hero-title-ar" value={settings.content?.hero_title_ar || ''} onChange={e => setSettings({...settings, content: {...settings.content, hero_title_ar: e.target.value}})} rows={2} className="border-[#D0D8DC] text-sm" dir="rtl" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'نص البطل (EN)' : 'Hero Subtitle (EN)'}</label>
                  <Textarea value={settings.content?.hero_sub_en || ''} onChange={e => setSettings({...settings, content: {...settings.content, hero_sub_en: e.target.value}})} rows={3} className="border-[#D0D8DC] text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'نص البطل (AR)' : 'Hero Subtitle (AR)'}</label>
                  <Textarea value={settings.content?.hero_sub_ar || ''} onChange={e => setSettings({...settings, content: {...settings.content, hero_sub_ar: e.target.value}})} rows={3} className="border-[#D0D8DC] text-sm" dir="rtl" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'زر البطل الرئيسي (EN)' : 'Primary CTA Label (EN)'}</label>
                  <Input value={settings.content?.hero_cta1_en || ''} onChange={e => setSettings({...settings, content: {...settings.content, hero_cta1_en: e.target.value}})} className="h-10 border-[#D0D8DC] text-sm" placeholder="Get a Free Consultation" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'زر البطل الرئيسي (AR)' : 'Primary CTA Label (AR)'}</label>
                  <Input value={settings.content?.hero_cta1_ar || ''} onChange={e => setSettings({...settings, content: {...settings.content, hero_cta1_ar: e.target.value}})} className="h-10 border-[#D0D8DC] text-sm" dir="rtl" placeholder="احجز استشارة مجانية" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'عنوان نموذج العميل (EN)' : 'Lead Form Title (EN)'}</label>
                  <Input value={settings.content?.lead_title_en || ''} onChange={e => setSettings({...settings, content: {...settings.content, lead_title_en: e.target.value}})} className="h-10 border-[#D0D8DC] text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'عنوان نموذج العميل (AR)' : 'Lead Form Title (AR)'}</label>
                  <Input value={settings.content?.lead_title_ar || ''} onChange={e => setSettings({...settings, content: {...settings.content, lead_title_ar: e.target.value}})} className="h-10 border-[#D0D8DC] text-sm" dir="rtl" />
                </div>
              </div>
              <Button data-testid="admin-save-homepage-content" onClick={() => saveSettings({ content: settings.content })} disabled={saving} className="mt-4 bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm">
                <Save className="w-4 h-4 me-2" />{saving ? '...' : (isAr ? 'حفظ محتوى الصفحة الرئيسية' : 'Save Homepage Content')}
              </Button>
            </Card>

            <Card title={isAr ? 'محتوى صفحة من نحن والفوتر' : 'About & Footer Content'} icon={FileText}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'قصة جُليد (EN)' : 'About Story (EN)'}</label>
                  <Textarea value={settings.content?.about_story_en || ''} onChange={e => setSettings({...settings, content: {...settings.content, about_story_en: e.target.value}})} rows={6} className="border-[#D0D8DC] text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'قصة جُليد (AR)' : 'About Story (AR)'}</label>
                  <Textarea value={settings.content?.about_story_ar || ''} onChange={e => setSettings({...settings, content: {...settings.content, about_story_ar: e.target.value}})} rows={6} className="border-[#D0D8DC] text-sm" dir="rtl" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'شعار الفوتر (EN)' : 'Footer Tagline (EN)'}</label>
                  <Input value={settings.content?.footer_tagline_en || ''} onChange={e => setSettings({...settings, content: {...settings.content, footer_tagline_en: e.target.value}})} className="h-10 border-[#D0D8DC] text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'شعار الفوتر (AR)' : 'Footer Tagline (AR)'}</label>
                  <Input value={settings.content?.footer_tagline_ar || ''} onChange={e => setSettings({...settings, content: {...settings.content, footer_tagline_ar: e.target.value}})} className="h-10 border-[#D0D8DC] text-sm" dir="rtl" />
                </div>
              </div>
              <Button onClick={() => saveSettings({ content: settings.content })} disabled={saving} className="mt-4 bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm">
                <Save className="w-4 h-4 me-2" />{saving ? '...' : (isAr ? 'حفظ المحتوى' : 'Save Content')}
              </Button>
            </Card>

            <Card title={isAr ? 'شهادات العملاء' : 'Client Testimonials'} icon={Users} action={
              <Button size="sm" variant="outline" className="border-[#D0D8DC] text-xs" data-testid="admin-add-testimonial"
                onClick={() => {
                  const list = Array.isArray(settings.content?.testimonials) ? [...settings.content.testimonials] : [];
                  list.push({ name: '', nationality_en: '', nationality_ar: '', property_en: '', property_ar: '', stars: 5, quote_en: '', quote_ar: '', img: '' });
                  setSettings({...settings, content: {...settings.content, testimonials: list}});
                }}>
                <Plus className="w-3.5 h-3.5 me-1" />{isAr ? 'إضافة شهادة' : 'Add Testimonial'}
              </Button>
            }>
              <p className="text-[11px] text-[#4A6572] mt-1 mb-3">{isAr ? 'اترك القائمة فارغة لاستخدام الشهادات الافتراضية.' : 'Leave the list empty to use default testimonials.'}</p>
              <div className="space-y-3">
                {(settings.content?.testimonials || []).map((t2, idx) => (
                  <div key={idx} data-testid={`admin-testimonial-${idx}`} className="p-4 bg-[#F8FAFB] rounded-xl border border-[#D0D8DC]/30">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-[#0B2F44]">#{idx + 1}</span>
                      <button onClick={() => {
                        const list = [...(settings.content?.testimonials || [])];
                        list.splice(idx, 1);
                        setSettings({...settings, content: {...settings.content, testimonials: list}});
                      }} className="text-red-500 hover:text-red-700" data-testid={`admin-testimonial-remove-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Name</label><Input value={t2.name || ''} onChange={e => {const list = [...settings.content.testimonials]; list[idx] = {...list[idx], name: e.target.value}; setSettings({...settings, content: {...settings.content, testimonials: list}});}} className="h-9 border-[#D0D8DC] text-xs" /></div>
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Image URL</label><Input value={t2.img || ''} onChange={e => {const list = [...settings.content.testimonials]; list[idx] = {...list[idx], img: e.target.value}; setSettings({...settings, content: {...settings.content, testimonials: list}});}} className="h-9 border-[#D0D8DC] text-xs" placeholder="https://..." /></div>
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Nationality (EN)</label><Input value={t2.nationality_en || ''} onChange={e => {const list = [...settings.content.testimonials]; list[idx] = {...list[idx], nationality_en: e.target.value}; setSettings({...settings, content: {...settings.content, testimonials: list}});}} className="h-9 border-[#D0D8DC] text-xs" /></div>
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Nationality (AR)</label><Input value={t2.nationality_ar || ''} onChange={e => {const list = [...settings.content.testimonials]; list[idx] = {...list[idx], nationality_ar: e.target.value}; setSettings({...settings, content: {...settings.content, testimonials: list}});}} dir="rtl" className="h-9 border-[#D0D8DC] text-xs" /></div>
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Property (EN)</label><Input value={t2.property_en || ''} onChange={e => {const list = [...settings.content.testimonials]; list[idx] = {...list[idx], property_en: e.target.value}; setSettings({...settings, content: {...settings.content, testimonials: list}});}} className="h-9 border-[#D0D8DC] text-xs" /></div>
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Property (AR)</label><Input value={t2.property_ar || ''} onChange={e => {const list = [...settings.content.testimonials]; list[idx] = {...list[idx], property_ar: e.target.value}; setSettings({...settings, content: {...settings.content, testimonials: list}});}} dir="rtl" className="h-9 border-[#D0D8DC] text-xs" /></div>
                      <div className="lg:col-span-2"><label className="text-[10px] text-[#4A6572] block mb-0.5">Quote (EN)</label><Textarea value={t2.quote_en || ''} onChange={e => {const list = [...settings.content.testimonials]; list[idx] = {...list[idx], quote_en: e.target.value}; setSettings({...settings, content: {...settings.content, testimonials: list}});}} rows={2} className="border-[#D0D8DC] text-xs" /></div>
                      <div className="lg:col-span-2"><label className="text-[10px] text-[#4A6572] block mb-0.5">Quote (AR)</label><Textarea value={t2.quote_ar || ''} onChange={e => {const list = [...settings.content.testimonials]; list[idx] = {...list[idx], quote_ar: e.target.value}; setSettings({...settings, content: {...settings.content, testimonials: list}});}} rows={2} dir="rtl" className="border-[#D0D8DC] text-xs" /></div>
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Stars (1-5)</label><Input type="number" min={1} max={5} value={t2.stars || 5} onChange={e => {const list = [...settings.content.testimonials]; list[idx] = {...list[idx], stars: parseInt(e.target.value || 5)}; setSettings({...settings, content: {...settings.content, testimonials: list}});}} className="h-9 border-[#D0D8DC] text-xs w-20" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button data-testid="admin-save-testimonials" onClick={() => saveSettings({ content: settings.content })} disabled={saving} className="mt-4 bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm">
                <Save className="w-4 h-4 me-2" />{saving ? '...' : (isAr ? 'حفظ الشهادات' : 'Save Testimonials')}
              </Button>
            </Card>

            <Card title={isAr ? 'المميزات (لماذا جُليد)' : 'Differentiators (Why Choose Guleed Mortgage)'} icon={Shield} action={
              <Button size="sm" variant="outline" className="border-[#D0D8DC] text-xs" data-testid="admin-add-differentiator"
                onClick={() => {
                  const list = Array.isArray(settings.content?.differentiators) ? [...settings.content.differentiators] : [];
                  list.push({ icon_key: 'shield', title_en: '', title_ar: '', desc_en: '', desc_ar: '' });
                  setSettings({...settings, content: {...settings.content, differentiators: list}});
                }}>
                <Plus className="w-3.5 h-3.5 me-1" />{isAr ? 'إضافة ميزة' : 'Add Differentiator'}
              </Button>
            }>
              <p className="text-[11px] text-[#4A6572] mt-1 mb-3">{isAr ? 'اترك القائمة فارغة لاستخدام المميزات الافتراضية الستة.' : 'Leave the list empty to use the 6 default differentiators.'}</p>
              <div className="space-y-3">
                {(settings.content?.differentiators || []).map((d, idx) => (
                  <div key={idx} data-testid={`admin-differentiator-${idx}`} className="p-4 bg-[#F8FAFB] rounded-xl border border-[#D0D8DC]/30">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-[#0B2F44]">#{idx + 1}</span>
                      <button onClick={() => {
                        const list = [...(settings.content?.differentiators || [])];
                        list.splice(idx, 1);
                        setSettings({...settings, content: {...settings.content, differentiators: list}});
                      }} className="text-red-500 hover:text-red-700" data-testid={`admin-differentiator-remove-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] text-[#4A6572] block mb-0.5">Icon</label>
                        <Select value={d.icon_key || 'shield'} onValueChange={v => {const list = [...settings.content.differentiators]; list[idx] = {...list[idx], icon_key: v}; setSettings({...settings, content: {...settings.content, differentiators: list}});}}>
                          <SelectTrigger className="h-9 border-[#D0D8DC] text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['shield','users','clock','cpu','globe','wifi','check'].map(k => <SelectItem key={k} value={k} className="text-xs capitalize">{k}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div></div>
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Title (EN)</label><Input value={d.title_en || ''} onChange={e => {const list = [...settings.content.differentiators]; list[idx] = {...list[idx], title_en: e.target.value}; setSettings({...settings, content: {...settings.content, differentiators: list}});}} className="h-9 border-[#D0D8DC] text-xs" /></div>
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Title (AR)</label><Input value={d.title_ar || ''} onChange={e => {const list = [...settings.content.differentiators]; list[idx] = {...list[idx], title_ar: e.target.value}; setSettings({...settings, content: {...settings.content, differentiators: list}});}} dir="rtl" className="h-9 border-[#D0D8DC] text-xs" /></div>
                      <div className="lg:col-span-2"><label className="text-[10px] text-[#4A6572] block mb-0.5">Description (EN)</label><Textarea value={d.desc_en || ''} onChange={e => {const list = [...settings.content.differentiators]; list[idx] = {...list[idx], desc_en: e.target.value}; setSettings({...settings, content: {...settings.content, differentiators: list}});}} rows={2} className="border-[#D0D8DC] text-xs" /></div>
                      <div className="lg:col-span-2"><label className="text-[10px] text-[#4A6572] block mb-0.5">Description (AR)</label><Textarea value={d.desc_ar || ''} onChange={e => {const list = [...settings.content.differentiators]; list[idx] = {...list[idx], desc_ar: e.target.value}; setSettings({...settings, content: {...settings.content, differentiators: list}});}} rows={2} dir="rtl" className="border-[#D0D8DC] text-xs" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button data-testid="admin-save-differentiators" onClick={() => saveSettings({ content: settings.content })} disabled={saving} className="mt-4 bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm">
                <Save className="w-4 h-4 me-2" />{saving ? '...' : (isAr ? 'حفظ المميزات' : 'Save Differentiators')}
              </Button>
            </Card>

            <Card title={isAr ? 'خطوات العمل (كيف نعمل)' : 'Process Steps (How It Works)'} icon={Target} action={
              <Button size="sm" variant="outline" className="border-[#D0D8DC] text-xs" data-testid="admin-add-step"
                onClick={() => {
                  const list = Array.isArray(settings.content?.steps) ? [...settings.content.steps] : [];
                  list.push({ num: list.length + 1, title_en: '', title_ar: '', desc_en: '', desc_ar: '' });
                  setSettings({...settings, content: {...settings.content, steps: list}});
                }}>
                <Plus className="w-3.5 h-3.5 me-1" />{isAr ? 'إضافة خطوة' : 'Add Step'}
              </Button>
            }>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'عنوان القسم (EN)' : 'Section Title (EN)'}</label>
                  <Input value={settings.content?.steps_title_en || ''} onChange={e => setSettings({...settings, content: {...settings.content, steps_title_en: e.target.value}})} placeholder="How It Works" className="h-9 border-[#D0D8DC] text-xs" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'عنوان القسم (AR)' : 'Section Title (AR)'}</label>
                  <Input value={settings.content?.steps_title_ar || ''} onChange={e => setSettings({...settings, content: {...settings.content, steps_title_ar: e.target.value}})} placeholder="كيف نعمل" dir="rtl" className="h-9 border-[#D0D8DC] text-xs" />
                </div>
              </div>
              <p className="text-[11px] text-[#4A6572] mt-3 mb-2">{isAr ? 'اترك القائمة فارغة لاستخدام الخطوات الخمس الافتراضية.' : 'Leave the list empty to use the 5 default steps.'}</p>
              <div className="space-y-3">
                {(settings.content?.steps || []).map((s, idx) => (
                  <div key={idx} data-testid={`admin-step-${idx}`} className="p-4 bg-[#F8FAFB] rounded-xl border border-[#D0D8DC]/30">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-[#0B2F44]">Step #{idx + 1}</span>
                      <button onClick={() => {
                        const list = [...(settings.content?.steps || [])];
                        list.splice(idx, 1);
                        setSettings({...settings, content: {...settings.content, steps: list}});
                      }} className="text-red-500 hover:text-red-700" data-testid={`admin-step-remove-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Number</label><Input type="number" min={1} value={s.num ?? idx + 1} onChange={e => {const list = [...settings.content.steps]; list[idx] = {...list[idx], num: parseInt(e.target.value || idx + 1)}; setSettings({...settings, content: {...settings.content, steps: list}});}} className="h-9 border-[#D0D8DC] text-xs w-20" /></div>
                      <div></div>
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Title (EN)</label><Input value={s.title_en || ''} onChange={e => {const list = [...settings.content.steps]; list[idx] = {...list[idx], title_en: e.target.value}; setSettings({...settings, content: {...settings.content, steps: list}});}} className="h-9 border-[#D0D8DC] text-xs" /></div>
                      <div><label className="text-[10px] text-[#4A6572] block mb-0.5">Title (AR)</label><Input value={s.title_ar || ''} onChange={e => {const list = [...settings.content.steps]; list[idx] = {...list[idx], title_ar: e.target.value}; setSettings({...settings, content: {...settings.content, steps: list}});}} dir="rtl" className="h-9 border-[#D0D8DC] text-xs" /></div>
                      <div className="lg:col-span-2"><label className="text-[10px] text-[#4A6572] block mb-0.5">Description (EN)</label><Textarea value={s.desc_en || ''} onChange={e => {const list = [...settings.content.steps]; list[idx] = {...list[idx], desc_en: e.target.value}; setSettings({...settings, content: {...settings.content, steps: list}});}} rows={2} className="border-[#D0D8DC] text-xs" /></div>
                      <div className="lg:col-span-2"><label className="text-[10px] text-[#4A6572] block mb-0.5">Description (AR)</label><Textarea value={s.desc_ar || ''} onChange={e => {const list = [...settings.content.steps]; list[idx] = {...list[idx], desc_ar: e.target.value}; setSettings({...settings, content: {...settings.content, steps: list}});}} rows={2} dir="rtl" className="border-[#D0D8DC] text-xs" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button data-testid="admin-save-steps" onClick={() => saveSettings({ content: settings.content })} disabled={saving} className="mt-4 bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm">
                <Save className="w-4 h-4 me-2" />{saving ? '...' : (isAr ? 'حفظ الخطوات' : 'Save Steps')}
              </Button>
            </Card>

            <Card title={isAr ? 'مقالات المدونة' : 'Blog Articles'} icon={PenTool} action={<Badge className="bg-[#0B2F44] text-white border-0 text-[10px]">{articles.length} {isAr ? 'مقالة' : 'articles'}</Badge>}>
              <div className="space-y-2 mt-3">
                {articles.map((a, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#F8FAFB] rounded-lg border border-[#D0D8DC]/30 hover:shadow-sm transition-shadow">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0B2F44] truncate">{isAr ? a.title_ar : a.title_en}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge className="bg-[#A68966]/10 text-[#A68966] border-0 text-[10px]">{isAr ? a.category_ar : a.category_en}</Badge>
                        <span className="text-[10px] text-[#4A6572]">{a.published_at}</span>
                        <span className="text-[10px] text-[#4A6572]">{a.read_time} min</span>
                        {a.faqs_en && <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">FAQ ✓</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ms-3">
                      <a href={`/blog/${a.slug}`} target="_blank" rel="noreferrer" className="text-[#4A6572] hover:text-[#0B2F44]"><Eye className="w-4 h-4" /></a>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            <Card title={isAr ? 'صفحات الموقع' : 'Site Pages'} icon={FileText}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-3">
                {['/', '/about', '/services', '/digital-assets', '/rates', '/blog', '/referral', '/contact', '/privacy', '/terms'].map(p => (
                  <a key={p} href={p} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2.5 bg-[#F8FAFB] rounded-lg border border-[#D0D8DC]/30 hover:border-[#A68966]/30 text-xs text-[#0B2F44] font-medium">
                    <ExternalLink className="w-3 h-3 text-[#A68966]" />{p === '/' ? 'Home' : p.replace('/', '')}
                  </a>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ═══ CAMPAIGNS ═══ */}
        {tab === 'campaigns' && <CampaignManager isAr={isAr} />}

        {/* ═══ BLOG EDITOR ═══ */}
        {tab === 'blog' && <BlogEditor isAr={isAr} />}

        {/* ═══ SOCIAL MEDIA ═══ */}
        {tab === 'social' && settings && (
          <Card title={isAr ? 'روابط التواصل الاجتماعي' : 'Social Media Links'} icon={Globe}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {[
                { key: 'linkedin', label: 'LinkedIn', ph: 'https://linkedin.com/company/gmre' },
                { key: 'instagram', label: 'Instagram', ph: 'https://instagram.com/gmre_uae' },
                { key: 'facebook', label: 'Facebook', ph: 'https://facebook.com/gmre' },
                { key: 'twitter', label: 'X (Twitter)', ph: 'https://x.com/gmre_uae' },
                { key: 'tiktok', label: 'TikTok', ph: 'https://tiktok.com/@gmre' },
                { key: 'youtube', label: 'YouTube', ph: 'https://youtube.com/@gmre' },
              ].map(s => (
                <div key={s.key}>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">{s.label}</label>
                  <Input value={settings.social_links?.[s.key] || ''} onChange={e => setSettings({...settings, social_links: {...settings.social_links, [s.key]: e.target.value}})} placeholder={s.ph} className="h-10 border-[#D0D8DC] text-sm" />
                </div>
              ))}
            </div>
            <Button onClick={() => saveSettings({ social_links: settings.social_links })} disabled={saving} className="mt-4 bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm">
              <Save className="w-4 h-4 me-2" />{saving ? (isAr ? 'جاري الحفظ...' : 'Saving...') : (isAr ? 'حفظ الروابط' : 'Save Links')}
            </Button>
          </Card>
        )}

        {/* ═══ SEO ═══ */}
        {tab === 'seo' && settings && (
          <div className="space-y-4">
            <Card title={isAr ? 'إعدادات SEO' : 'SEO Settings'} icon={Search}>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#4A6572] font-medium block mb-1">Meta Title (EN)</label>
                    <Input value={settings.seo?.meta_title_en || ''} onChange={e => setSettings({...settings, seo: {...settings.seo, meta_title_en: e.target.value}})} className="h-10 border-[#D0D8DC] text-sm" />
                    <p className="text-[10px] text-[#4A6572]/60 mt-0.5">{(settings.seo?.meta_title_en || '').length}/60</p>
                  </div>
                  <div>
                    <label className="text-xs text-[#4A6572] font-medium block mb-1">Meta Title (AR)</label>
                    <Input value={settings.seo?.meta_title_ar || ''} onChange={e => setSettings({...settings, seo: {...settings.seo, meta_title_ar: e.target.value}})} className="h-10 border-[#D0D8DC] text-sm" dir="rtl" />
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#4A6572] font-medium block mb-1">Meta Description (EN)</label>
                    <Textarea value={settings.seo?.meta_desc_en || ''} onChange={e => setSettings({...settings, seo: {...settings.seo, meta_desc_en: e.target.value}})} rows={2} className="border-[#D0D8DC] text-sm" />
                    <p className="text-[10px] text-[#4A6572]/60 mt-0.5">{(settings.seo?.meta_desc_en || '').length}/155</p>
                  </div>
                  <div>
                    <label className="text-xs text-[#4A6572] font-medium block mb-1">Meta Description (AR)</label>
                    <Textarea value={settings.seo?.meta_desc_ar || ''} onChange={e => setSettings({...settings, seo: {...settings.seo, meta_desc_ar: e.target.value}})} rows={2} className="border-[#D0D8DC] text-sm" dir="rtl" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">Google Analytics ID</label>
                  <Input value={settings.google_analytics_id || ''} onChange={e => setSettings({...settings, google_analytics_id: e.target.value})} placeholder="G-XXXXXXXXXX" className="h-10 border-[#D0D8DC] text-sm max-w-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#4A6572] font-medium block mb-1">Meta Pixel ID</label>
                  <Input value={settings.meta_pixel_id || ''} onChange={e => setSettings({...settings, meta_pixel_id: e.target.value})} placeholder="123456789" className="h-10 border-[#D0D8DC] text-sm max-w-sm" />
                </div>
              </div>
              <Button onClick={() => saveSettings({ seo: settings.seo, google_analytics_id: settings.google_analytics_id, meta_pixel_id: settings.meta_pixel_id })} disabled={saving} className="mt-4 bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm">
                <Save className="w-4 h-4 me-2" />{saving ? '...' : (isAr ? 'حفظ SEO' : 'Save SEO')}
              </Button>
            </Card>
            <Card title={isAr ? 'حالة SEO التقنية' : 'Technical SEO Status'} icon={Shield}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                {[
                  { label: 'Sitemap.xml', status: 'active', url: '/api/sitemap.xml' },
                  { label: 'Robots.txt', status: 'active', url: '/api/robots.txt' },
                  { label: 'Schema.org', status: 'active', url: null },
                  { label: 'FAQ Schema', status: 'active', url: null },
                  { label: 'Hreflang EN/AR', status: 'active', url: null },
                  { label: 'Blog SEO (5)', status: 'active', url: '/blog' },
                  { label: 'GA4', status: settings.google_analytics_id ? 'active' : 'pending' },
                  { label: 'Meta Pixel', status: settings.meta_pixel_id ? 'active' : 'pending' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 p-3 bg-[#F8FAFB] rounded-lg border border-[#D0D8DC]/30">
                    <div className={`w-2 h-2 rounded-full ${item.status === 'active' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                    <span className="text-xs text-[#0B2F44] font-medium">{item.label}</span>
                    {item.url && <a href={item.url} target="_blank" rel="noreferrer" className="ms-auto"><ExternalLink className="w-3 h-3 text-[#4A6572]" /></a>}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ═══ SETTINGS ═══ */}
        {tab === 'settings' && settings && (
          <Card title={isAr ? 'إعدادات الموقع العامة' : 'General Site Settings'} icon={Settings}>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'الهاتف' : 'Phone'}</label><Input data-testid="admin-phone" value={settings.phone || ''} onChange={e => setSettings({...settings, phone: e.target.value})} className="h-10 border-[#D0D8DC] text-sm" /></div>
                <div><label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'البريد الإلكتروني' : 'Email'}</label><Input data-testid="admin-email" value={settings.email || ''} onChange={e => setSettings({...settings, email: e.target.value})} className="h-10 border-[#D0D8DC] text-sm" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'رقم واتساب' : 'WhatsApp Number'}</label><Input data-testid="admin-whatsapp" value={settings.whatsapp || ''} onChange={e => setSettings({...settings, whatsapp: e.target.value})} className="h-10 border-[#D0D8DC] text-sm" placeholder="+971522898249" /></div>
                <div><label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'رابط Calendly' : 'Calendly URL'}</label><Input value={settings.calendly_url || ''} onChange={e => setSettings({...settings, calendly_url: e.target.value})} placeholder="https://calendly.com/gmre" className="h-10 border-[#D0D8DC] text-sm" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'العنوان (EN)' : 'Address (EN)'}</label><Input value={settings.address_en || ''} onChange={e => setSettings({...settings, address_en: e.target.value})} className="h-10 border-[#D0D8DC] text-sm" /></div>
                <div><label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'العنوان (AR)' : 'Address (AR)'}</label><Input value={settings.address_ar || ''} onChange={e => setSettings({...settings, address_ar: e.target.value})} className="h-10 border-[#D0D8DC] text-sm" dir="rtl" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'ساعات العمل (EN)' : 'Business Hours (EN)'}</label><Input value={settings.business_hours_en || ''} onChange={e => setSettings({...settings, business_hours_en: e.target.value})} className="h-10 border-[#D0D8DC] text-sm" placeholder="Sunday – Saturday · 8:00 AM – 8:00 PM GST" /></div>
                <div><label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'ساعات العمل (AR)' : 'Business Hours (AR)'}</label><Input value={settings.business_hours_ar || ''} onChange={e => setSettings({...settings, business_hours_ar: e.target.value})} className="h-10 border-[#D0D8DC] text-sm" dir="rtl" /></div>
              </div>
            </div>

            {/* ─── Maintenance Mode ─── */}
            <div className="mt-8 pt-6 border-t border-[#D0D8DC]/40">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h4 className="text-sm font-bold text-[#0B2F44] flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${settings.maintenance_mode ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`}></span>
                    {isAr ? 'وضع التطوير (الموقع تحت الصيانة)' : 'Maintenance Mode'}
                  </h4>
                  <p className="text-xs text-[#4A6572] mt-1 max-w-xl">
                    {isAr ? 'عند التفعيل، يرى الزوار صفحة "تحت التطوير" فقط. الأدمن يبقى قادراً على دخول الموقع كاملاً.' : 'When enabled, public visitors see only the "Under Development" page. Admins keep full access to manage the site.'}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!settings.maintenance_mode}
                  data-testid="admin-maintenance-toggle"
                  onClick={() => setSettings({ ...settings, maintenance_mode: !settings.maintenance_mode })}
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition ${settings.maintenance_mode ? 'bg-amber-500' : 'bg-[#D0D8DC]'}`}
                >
                  <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition ${settings.maintenance_mode ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              {settings.maintenance_mode && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'رسالة الصيانة (EN)' : 'Maintenance Message (EN)'}</label>
                    <textarea data-testid="admin-maintenance-msg-en" rows={3} value={settings.maintenance_message_en || ''} onChange={e => setSettings({...settings, maintenance_message_en: e.target.value})} className="w-full border border-[#D0D8DC] rounded px-3 py-2 text-sm" placeholder="We are crafting an elevated Guleed Mortgage experience…" />
                  </div>
                  <div>
                    <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'رسالة الصيانة (AR)' : 'Maintenance Message (AR)'}</label>
                    <textarea data-testid="admin-maintenance-msg-ar" rows={3} value={settings.maintenance_message_ar || ''} onChange={e => setSettings({...settings, maintenance_message_ar: e.target.value})} className="w-full border border-[#D0D8DC] rounded px-3 py-2 text-sm" dir="rtl" placeholder="نعمل حالياً على تطوير تجربة Guleed Mortgage…" />
                  </div>
                  <div>
                    <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'العودة المرتقبة (EN)' : 'Expected Return (EN)'}</label>
                    <Input value={settings.maintenance_eta_en || ''} onChange={e => setSettings({...settings, maintenance_eta_en: e.target.value})} className="h-10 border-[#D0D8DC] text-sm" placeholder="Relaunch imminent" />
                  </div>
                  <div>
                    <label className="text-xs text-[#4A6572] font-medium block mb-1">{isAr ? 'العودة المرتقبة (AR)' : 'Expected Return (AR)'}</label>
                    <Input value={settings.maintenance_eta_ar || ''} onChange={e => setSettings({...settings, maintenance_eta_ar: e.target.value})} className="h-10 border-[#D0D8DC] text-sm" dir="rtl" placeholder="العودة المرتقبة قريباً" />
                  </div>
                </div>
              )}
            </div>

            <Button data-testid="admin-save-settings" onClick={() => saveSettings({ phone: settings.phone, email: settings.email, whatsapp: settings.whatsapp, calendly_url: settings.calendly_url, address_en: settings.address_en, address_ar: settings.address_ar, business_hours_en: settings.business_hours_en, business_hours_ar: settings.business_hours_ar, maintenance_mode: !!settings.maintenance_mode, maintenance_message_en: settings.maintenance_message_en, maintenance_message_ar: settings.maintenance_message_ar, maintenance_eta_en: settings.maintenance_eta_en, maintenance_eta_ar: settings.maintenance_eta_ar })} disabled={saving} className="mt-6 bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm">
              <Save className="w-4 h-4 me-2" />{saving ? '...' : (isAr ? 'حفظ الإعدادات' : 'Save Settings')}
            </Button>
          </Card>
        )}

        {/* ═══ RATES ═══ */}
        {tab === 'rates' && (
          <div className="space-y-4">
            {['resident', 'non_resident'].map(section => (
              <Card key={section} title={section === 'resident' ? (isAr ? 'أسعار المقيمين' : 'Resident Rates') : (isAr ? 'أسعار غير المقيمين' : 'Non-Resident Rates')} icon={Percent} action={
                <Button size="sm" variant="outline" className="border-[#D0D8DC] text-xs" data-testid={`admin-add-rate-${section}`}
                  onClick={() => {
                    const list = [...(rates[section] || [])];
                    list.push({ product: '', rate_type: '', best_rate: '', notes: '' });
                    setRates({ ...rates, [section]: list });
                  }}>
                  <Plus className="w-3.5 h-3.5 me-1" />{isAr ? 'إضافة صف' : 'Add Row'}
                </Button>
              }>
                <div className="overflow-x-auto mt-3">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F8FAFB]">
                        <th className="px-2 py-2 text-start text-[10px] font-semibold text-[#4A6572] uppercase">Product</th>
                        <th className="px-2 py-2 text-start text-[10px] font-semibold text-[#4A6572] uppercase">Rate Type</th>
                        <th className="px-2 py-2 text-start text-[10px] font-semibold text-[#4A6572] uppercase">Best Rate</th>
                        <th className="px-2 py-2 text-start text-[10px] font-semibold text-[#4A6572] uppercase">Notes</th>
                        <th className="px-2 py-2 w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {(rates[section] || []).map((row, idx) => (
                        <tr key={idx} data-testid={`admin-rate-${section}-${idx}`} className="border-b border-[#D0D8DC]/20">
                          <td className="p-1.5"><Input value={row.product || ''} onChange={e => {const list = [...rates[section]]; list[idx] = {...list[idx], product: e.target.value}; setRates({...rates, [section]: list});}} className="h-8 border-[#D0D8DC] text-xs" /></td>
                          <td className="p-1.5"><Input value={row.rate_type || ''} onChange={e => {const list = [...rates[section]]; list[idx] = {...list[idx], rate_type: e.target.value}; setRates({...rates, [section]: list});}} className="h-8 border-[#D0D8DC] text-xs" /></td>
                          <td className="p-1.5"><Input value={row.best_rate || ''} onChange={e => {const list = [...rates[section]]; list[idx] = {...list[idx], best_rate: e.target.value}; setRates({...rates, [section]: list});}} className="h-8 border-[#D0D8DC] text-xs w-28" /></td>
                          <td className="p-1.5"><Input value={row.notes || ''} onChange={e => {const list = [...rates[section]]; list[idx] = {...list[idx], notes: e.target.value}; setRates({...rates, [section]: list});}} className="h-8 border-[#D0D8DC] text-xs" /></td>
                          <td className="p-1.5">
                            <button onClick={() => {const list = [...rates[section]]; list.splice(idx, 1); setRates({...rates, [section]: list});}} className="text-red-500 hover:text-red-700" data-testid={`admin-rate-remove-${section}-${idx}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ))}
            <div className="flex items-center gap-3">
              <Button data-testid="admin-save-rates" onClick={saveRates} disabled={saving} className="bg-[#0B2F44] hover:bg-[#0D3A54] text-white text-sm">
                <Save className="w-4 h-4 me-2" />{saving ? '...' : (isAr ? 'حفظ جميع الأسعار' : 'Save All Rates')}
              </Button>
              <Button data-testid="admin-reset-rates" onClick={resetRates} disabled={saving} variant="outline" className="border-[#D0D8DC] text-sm text-[#4A6572]">
                <RefreshCw className="w-4 h-4 me-2" />{isAr ? 'استعادة الافتراضي' : 'Reset to Defaults'}
              </Button>
            </div>
          </div>
        )}

        {/* ═══ DATA TABLES ═══ */}
        {tab === 'leads' && <LeadsTable leads={leads} isAr={isAr} df={df} update={updateLeadStatus} />}
        {tab === 'subscribers' && <DataTable data={subscribers} columns={['email', 'source', 'lead_magnet', 'created_at']} isAr={isAr} />}
        {tab === 'contacts' && <DataTable data={contacts} columns={['name', 'email', 'subject', 'status', 'created_at']} isAr={isAr} />}
        {tab === 'chatbot' && <DataTable data={chatbotLeads} columns={['phone', 'status', 'created_at']} isAr={isAr} />}
        {tab === 'referrals' && <DataTable data={referrals} columns={['full_name', 'email', 'professional_type', 'referral_code', 'status', 'created_at']} isAr={isAr} />}
      </div>
    </div>
  );
}

/* ═══ Reusable Components ═══ */

function KPI({ icon: Icon, label, value, dark, color }) {
  const bg = dark ? 'bg-[#0B2F44]' : color === 'blue' ? 'bg-blue-50' : color === 'emerald' ? 'bg-emerald-50' : 'bg-purple-50';
  const txt = dark ? 'text-white' : color === 'blue' ? 'text-blue-900' : color === 'emerald' ? 'text-emerald-900' : 'text-purple-900';
  const ic = dark ? 'text-[#C9A84C]' : color === 'blue' ? 'text-blue-600' : color === 'emerald' ? 'text-emerald-600' : 'text-purple-600';
  const sub = dark ? 'text-white/60' : 'text-[#4A6572]';
  return (
    <div className={`${bg} rounded-xl p-4 ${!dark ? 'border border-[#D0D8DC]/50' : ''}`}>
      <Icon className={`w-5 h-5 ${ic} mb-2`} />
      <p className={`text-xl font-bold ${txt}`} style={{ fontFamily: 'var(--font-en-mono)' }}>{value}</p>
      <p className={`text-[10px] mt-0.5 ${sub}`}>{label}</p>
    </div>
  );
}

function Card({ title, icon: Icon, children, action }) {
  return (
    <div className="bg-white rounded-xl p-5 border border-[#D0D8DC]/50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-[#A68966]" /><span className="text-sm font-semibold text-[#0B2F44]">{title}</span></div>
        {action}
      </div>
      {children}
    </div>
  );
}

function SourceBar({ label, value, max }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="py-1">
      <div className="flex justify-between text-[10px] mb-0.5"><span className="text-[#4A6572] capitalize">{label}</span><span className="font-bold text-[#0B2F44]">{value}</span></div>
      <div className="h-1.5 bg-[#F8FAFB] rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#A68966] to-[#C9A84C] rounded-full" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function LeadsTable({ leads, isAr, df, update }) {
  return (
    <div className="bg-white rounded-xl border border-[#D0D8DC]/50 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="bg-[#0B2F44]">
            {['Name', 'Phone', 'Type', 'Status', 'Date', 'Action'].map(h => <th key={h} className="px-3 py-2.5 text-start text-[10px] font-semibold text-white uppercase">{h}</th>)}
          </tr></thead>
          <tbody>
            {leads.map((l, i) => (
              <tr key={i} className={`border-b border-[#D0D8DC]/20 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFB]'}`}>
                <td className="px-3 py-2.5 text-xs font-medium text-[#0B2F44]">{l.full_name}</td>
                <td className="px-3 py-2.5 text-xs text-[#4A6572]">{l.phone}</td>
                <td className="px-3 py-2.5 text-xs text-[#4A6572]">{l.enquiry_type}</td>
                <td className="px-3 py-2.5"><Badge className={`text-[10px] border-0 ${l.status === 'new' ? 'bg-blue-50 text-blue-700' : l.status === 'funded' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>{l.status}</Badge></td>
                <td className="px-3 py-2.5 text-[10px] text-[#4A6572]">{l.created_at ? new Date(l.created_at).toLocaleDateString() : '—'}</td>
                <td className="px-3 py-2.5">
                  <Select value={l.status} onValueChange={v => update(l.phone, v)}>
                    <SelectTrigger className="h-7 text-[10px] w-24 border-[#D0D8DC]"><SelectValue /></SelectTrigger>
                    <SelectContent>{PIPELINE_STAGES.map(s => <SelectItem key={s.id} value={s.id} className="text-[10px]">{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {leads.length === 0 && <div className="p-8 text-center text-[#4A6572] text-xs">No data</div>}
    </div>
  );
}

function DataTable({ data, columns, isAr }) {
  if (!data.length) return <div className="bg-white rounded-xl p-10 border border-[#D0D8DC]/50 text-center text-[#4A6572] text-xs">{isAr ? 'لا توجد بيانات' : 'No data yet'}</div>;
  return (
    <div className="bg-white rounded-xl border border-[#D0D8DC]/50 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="bg-[#0B2F44]">{columns.map(c => <th key={c} className="px-3 py-2.5 text-start text-[10px] font-semibold text-white uppercase">{c.replace(/_/g, ' ')}</th>)}</tr></thead>
          <tbody>{data.map((r, i) => (
            <tr key={i} className={`border-b border-[#D0D8DC]/20 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFB]'}`}>
              {columns.map(c => <td key={c} className="px-3 py-2.5 text-xs text-[#1A2B35]">
                {c === 'status' ? <Badge className={`${r[c] === 'new' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'} border-0 text-[10px]`}>{r[c] || '—'}</Badge>
                : c === 'created_at' ? <span className="text-[10px] text-[#4A6572]">{r[c] ? new Date(r[c]).toLocaleString() : '—'}</span>
                : <span className="truncate block max-w-[180px]">{r[c] || '—'}</span>}
              </td>)}
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
